import { LoadingOutlined } from "@ant-design/icons";
import { IconCheck } from "@tabler/icons-react";
import { Collapse, Flex, Grid, Typography, theme } from "antd";
import { format } from "date-fns";
import type { WorklogEntryType } from "@/common/artemis-sdk";
import {
	type ArtemisTask,
	TaskStatus,
	type WorklogEntry,
} from "@/common/model";

/** Short author labels for the dense rows (the badge map's "Agent Output"/"User Input" are too wide). */
const WORKLOG_SHORT_LABEL: Record<WorklogEntryType, string> = {
	AGENT_OUTPUT: "Agent",
	SYSTEM: "System",
	USER_INPUT: "User",
};

/** Fixed width so the time and text columns line up across rows. */
const LABEL_WIDTH = 56;

function sortByCreatedAtAsc(worklog: WorklogEntry[]) {
	return [...worklog].sort(
		(a, b) =>
			+new Date(a.createdAt as string) - +new Date(b.createdAt as string),
	);
}

/**
 * A task's worklog as a dense, collapsible log, sized to sit as a row in the task's details. Collapsed
 * by default, the header shows the entry count and the latest entry. Each step is one line
 * (✓ · who · time · text, or just ✓ · text on narrow screens); long text expands in place. A spinner in the header and a trailing
 * "Thinking…" row mark a task that is still WORKING.
 */
export function TaskWorklog({ task }: { task: ArtemisTask }) {
	const {
		token: { colorSuccess },
	} = theme.useToken();
	// On narrow screens rows drop the who/time columns: just ✓ + text on one line.
	const isNarrow = !Grid.useBreakpoint().md;

	if (!task?.worklog?.length)
		return <Typography.Text type="secondary">-</Typography.Text>;

	const entries = sortByCreatedAtAsc(task.worklog);
	const latest = entries[entries.length - 1];
	const isWorking = task.status === TaskStatus.WORKING;

	const label = (
		<Flex gap={8} align="baseline" style={{ minWidth: 0 }}>
			<Typography.Text style={{ flexShrink: 0 }}>
				{entries.length} {entries.length === 1 ? "entry" : "entries"}
			</Typography.Text>
			{isWorking && <LoadingOutlined />}
			<Typography.Text type="secondary" ellipsis style={{ minWidth: 0 }}>
				{!isNarrow &&
					`${format(new Date(latest.createdAt as string), "h:mm a")} · `}
				{latest.text}
			</Typography.Text>
		</Flex>
	);

	// The worklog grows over a task's life, so cap the height and scroll (scrollbar hidden — see styles.css).
	const body = (
		<Flex vertical gap={2} className="scroll-no-bar" style={{ maxHeight: 360 }}>
			{entries.map((item) => (
				<Flex key={item.id} gap={8} align="baseline">
					<IconCheck
						size={14}
						stroke={2.5}
						color={colorSuccess}
						// Pinned to the first line (22px line height − 14px icon) so it stays on top when a row expands.
						style={{ flexShrink: 0, alignSelf: "flex-start", marginTop: 4 }}
					/>
					{!isNarrow && (
						<>
							<Typography.Text
								strong
								style={{
									flexShrink: 0,
									width: LABEL_WIDTH,
									whiteSpace: "nowrap",
								}}
							>
								{WORKLOG_SHORT_LABEL[item.type]}
							</Typography.Text>
							<Typography.Text
								type="secondary"
								style={{ flexShrink: 0, whiteSpace: "nowrap" }}
							>
								{format(new Date(item.createdAt as string), "MMM d, h:mm a")}
							</Typography.Text>
						</>
					)}
					<Typography.Paragraph
						ellipsis={{ rows: 1, expandable: "collapsible" }}
						style={{
							whiteSpace: "pre-wrap",
							marginBottom: 0,
							flex: 1,
							minWidth: 0,
						}}
					>
						{item.text}
					</Typography.Paragraph>
				</Flex>
			))}
			{isWorking && (
				<Flex gap={8} align="baseline">
					<LoadingOutlined style={{ width: 14 }} />
					<Typography.Text type="secondary">Thinking…</Typography.Text>
				</Flex>
			)}
		</Flex>
	);

	// Zero header/body padding so the collapsed line aligns with the other detail rows. The details
	// table sizes cells to their content, and one-line ellipsis text would widen it past the viewport;
	// a zero-width box stretched to 100% lets the ellipsis truncate instead.
	return (
		<div style={{ width: 0, minWidth: "100%" }}>
			<Collapse
				ghost
				size="small"
				// title minWidth 0 lets the latest-entry preview in the header ellipsize.
				styles={{
					header: { padding: 0 },
					title: { minWidth: 0 },
					body: { padding: "6px 0 0" },
				}}
				items={[{ key: "activity", label, children: body }]}
			/>
		</div>
	);
}
